import React from 'react';
import { useTranslation } from '../../context/LanguageContext';
import { usePublicCollection } from '../../hooks/usePublicCollection';
import { TEAM } from '../../services/publicContent';
import { Layout } from '../layout/Layout';
import { FaUserMd } from 'react-icons/fa';
import { SEOHead } from '../SEOHead';
import { buildBreadcrumbSchema } from '../../utils/seo';
import { useLocalizedField } from '../../hooks/useLocalizedField';

// TeamRow — single flat-list row: photo left, info right
const TeamRow = React.memo(({ member, pick, even }) => (
    <div className={`${even ? 'bg-white' : 'bg-gray-50'}`}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 md:py-12">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 sm:gap-8 md:gap-10">
                {/* Photo */}
                <div className="flex-shrink-0 w-44 sm:w-52 md:w-64 aspect-[3/4] rounded-2xl overflow-hidden bg-gray-100 shadow-md">
                    {member.photo ? (
                        <img
                            src={member.photo}
                            alt={pick(member, 'name')}
                            className="w-full h-full object-cover object-top"
                            draggable={false}
                            loading="lazy"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center">
                            <FaUserMd className="text-5xl sm:text-6xl text-gray-300" />
                        </div>
                    )}
                </div>

                {/* Info */}
                <div className="flex-1 text-center sm:text-left">
                    <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-1">
                        {pick(member, 'name')}
                    </h3>
                    <p className="text-sm sm:text-base font-semibold text-gold mb-3">
                        {pick(member, 'specialty')}
                    </p>
                    {(member.description || member.descriptionEn) && (
                        <p className="text-sm sm:text-base text-gray-500 leading-relaxed whitespace-pre-line">
                            {pick(member, 'description')}
                        </p>
                    )}
                </div>
            </div>
        </div>
    </div>
));

// Team page
export const Team = () => {
    const { t } = useTranslation();
    const pick = useLocalizedField();
    const { items: members, loading, error } = usePublicCollection(TEAM);

    // SEO
    const seoTitle = t('seo.teamTitle');
    const seoDescription = t('seo.teamDescription');

    return (
        <Layout className="min-h-screen flex flex-col overflow-x-hidden">
            <SEOHead
                title={seoTitle}
                description={seoDescription}
                path="/team"
                schema={[
                    buildBreadcrumbSchema([
                        { name: t('nav.home'), path: '/' },
                        { name: t('nav.team'), path: '/team' },
                    ]),
                    {
                        '@type': 'MedicalOrganization',
                        name: 'UDent',
                        url: 'https://udent.com.ua/team',
                        description: seoDescription,
                        member: members.map(member => ({
                            '@type': 'Dentist',
                            name: pick(member, 'name'),
                            jobTitle: pick(member, 'specialty'),
                            ...(member.photo ? { image: member.photo } : {}),
                        })),
                    },
                ]}
            />

            {/* Page header */}
            <section className="bg-navy py-12 sm:py-16 md:py-20">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3">
                        {t('team.title')}
                    </h1>
                    <p className="text-base sm:text-lg text-gray-300 max-w-2xl mx-auto">
                        {t('team.subtitle')}
                    </p>
                </div>
            </section>

            {/* Team list */}
            <h2 className="sr-only">{t('team.subtitle')}</h2>
            {loading ? (
                <div className="flex justify-center items-center min-h-[60vh] bg-gray-50">
                    <div className="w-10 h-10 border-4 border-gold border-t-transparent rounded-full animate-spin" />
                </div>
            ) : error ? (
                <div className="text-center py-20 bg-gray-50">
                    <p className="text-gray-600">{t('errors.team')}</p>
                </div>
            ) : members.length === 0 ? (
                <div className="text-center py-20 bg-gray-50">
                    <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                        <FaUserMd className="w-10 h-10 text-gray-400" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-700 mb-2">{t('team.empty')}</h3>
                    <p className="text-gray-500">{t('team.emptyDesc')}</p>
                </div>
            ) : (
                <div className="divide-y divide-gray-200">
                    {members.map((member, idx) => (
                        <TeamRow
                            key={member.id}
                            member={member}
                            pick={pick}
                            even={idx % 2 === 0}
                        />
                    ))}
                </div>
            )}
        </Layout>
    );
};

